import { z } from 'zod';
import { sharingTypeSchema } from './schema';

const analyticsStatusSchema = z.enum(['CREATED', 'UPLOADED', 'UPLOAD_FAILED']);

export type TAnalyticsRecordStatus = z.infer<typeof analyticsStatusSchema>;

export enum EAnalyticsMetrics {
  CUSTOMER = 'customer',
  SESSION = 'session',
  RATING = 'rating',
}

export enum ESessionEvents {
  SESSION_CREATED = 'session_created',
  SESSION_STARTED = 'session_started',
  SESSION_UPDATED = 'session_updated',
  SESSION_ENDED = 'session_ended',
}

// CustomerEvents corresponds to events fired while a sharing url is in use
export enum ECustomerEvents {
  WAITING_ROOM_OPENED = 'waiting_room_opened',
  STREAM_UPDATED = 'stream_updated',
}

export enum ERatingEvents {
  RATING_EXTENDED = 'rating_extended',
}

const analyticsMetricsSchema = z.enum(EAnalyticsMetrics);
const analyticsSessionEventsSchema = z.enum(ESessionEvents);
const analyticsCustomerEventsSchema = z.enum(ECustomerEvents);
// const analyticsRatingEventsSchema = z.nativeEnum(ERatingEvents);

export const customerSchema = z.object({
  action: analyticsCustomerEventsSchema,
  sessionId: z.string(),
  voipUsed: z.boolean().prefault(false),
  mmToken: z.string().prefault(''),
  deviceType: z.string().prefault(''),
  deviceBrand: z.string().prefault(''),
  browserName: z.string().prefault(''),
  browserVersion: z.string().prefault(''),
  osName: z.string().prefault(''),
  screenWidth: z.string().prefault(''),
  screenHeight: z.string().prefault(''),
  timestamp: z.string().prefault(''),
});

const carSelectionMethods = z.enum(['vin', 'code', 'dcc', 'carline']);
const dgaGenerationLocations = z.enum(['dashboard', 'end']);
const dealerTools = z.enum([
  'copyUrl',
  'dcc',
  'customerAllowance',
  'scc',
  'createDGA',
  'copyDGAUrl',
  'downloadImage',
  'downloadAllImages',
  'downloadComparisonImage',
  'downloadAllComparisonImages',
]);

export type TCarSelectionMethod = z.infer<typeof carSelectionMethods>;
export type TDgaGenerationLocation = z.infer<typeof dgaGenerationLocations>;
export type TDealerTool = z.infer<typeof dealerTools>;

export const sessionSchema = z.object({
  sessionId: z.string(),
  status: analyticsSessionEventsSchema,
  virtualExhibitionType: z.union([sharingTypeSchema, z.literal('')]).prefault(''),
  dealerIdMat: z.string().prefault(''),
  dealerIdKvps: z.string().prefault(''),
  country: z.string().prefault(''),
  voipUsed: z.boolean().prefault(false), // true if either mic or cam was enabled by dealer in the session
  timezone: z.string(),
  mmToken: z.string().prefault(''),
  url: z.string().prefault(''),
  carSelectionMethod: z.union([carSelectionMethods, z.literal('')]).prefault(''),
  carModel: z.string().prefault(''),
  dgaGeneratedAt: z.union([dgaGenerationLocations, z.literal('')]).prefault(''),
  dealerToolUsed: z.union([dealerTools, z.literal('')]).prefault(''),
  prString: z.string().prefault(''),
  hasTimedOut: z.boolean().prefault(false),
  tenant: z.string().prefault(''), // frontend sends partnerToken, will be replaced with tenant name in the backend
  experience2DUsed: z.boolean().prefault(false),
  experience3DUsed: z.boolean().prefault(false),
  comparisonUsed: z.boolean().prefault(false),
  isVtp: z.boolean().prefault(false), // true if the vehicle was resolved via VTP API (stock car)
  timestamp: z.string().prefault(''),
});

export const ratingSchema = z.object({
  sessionId: z.string(),
  mmToken: z.string().prefault(''),
  rating: z.string().prefault(''),
  comment: z.string().prefault(''),
  user: z.string().prefault(''),
  timestamp: z.string().prefault(''),
});

export type IAnalyticsSessionData = z.infer<typeof sessionSchema>;
export type IAnalyticsCustomerData = z.infer<typeof customerSchema>;
export type IAnalyticsRatingData = z.infer<typeof ratingSchema>;

export type TAnalyticsData = IAnalyticsSessionData | IAnalyticsCustomerData | IAnalyticsRatingData;

export const analyticsRecordSchema = z.object({
  id: z.string().prefault(() => crypto.randomUUID()),
  type: analyticsMetricsSchema,
  status: analyticsStatusSchema,
  data: z.union([customerSchema, sessionSchema, ratingSchema]),
  timestamp: z.number().optional(),
  expiresAfter: z.number().optional(),
});

export type IAnalyticsRecord = z.infer<typeof analyticsRecordSchema>;
